import { useState } from "react";
import { api, formatMoney, formatCPF } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Search, GitCompare, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/** Agrupa os campos exibidos por seção para render organizado. */
const SECTIONS = [
  { title: "Identificação", keys: ["protocolo", "nome_completo", "cpf", "data_nascimento", "sexo", "estado_civil", "status"] },
  { title: "Contato & Endereço", keys: ["telefone", "email", "bairro", "cidade", "tipo_moradia", "area_de_risco"] },
  { title: "Família", keys: ["qtd_moradores", "qtd_dependentes", "qtd_criancas", "qtd_idosos", "qtd_pcd", "possui_pcd", "possui_idoso"] },
  { title: "Renda & Elegibilidade", keys: ["renda_responsavel", "renda_conjuge", "outras_rendas", "renda_total", "recebe_bolsa_familia", "apta_faixa1"] },
  { title: "Documentos", keys: ["documentos_qtd", "documentos_tipos", "documentos_faltando", "incompleto"] },
];

const LABELS = {
  protocolo: "Protocolo", nome_completo: "Nome", cpf: "CPF",
  data_nascimento: "Data de nascimento", sexo: "Sexo", estado_civil: "Estado civil",
  telefone: "Telefone", email: "E-mail", bairro: "Bairro", cidade: "Cidade",
  tipo_moradia: "Tipo de moradia", area_de_risco: "Área de risco",
  qtd_moradores: "Qtd. moradores", qtd_dependentes: "Dependentes",
  qtd_criancas: "Crianças", qtd_idosos: "Idosos", qtd_pcd: "PcD",
  possui_pcd: "Possui PcD", possui_idoso: "Possui idoso",
  renda_responsavel: "Renda do responsável", renda_conjuge: "Renda do cônjuge",
  outras_rendas: "Outras rendas", renda_total: "Renda total",
  recebe_bolsa_familia: "Bolsa Família", apta_faixa1: "Apta Faixa 1",
  status: "Status", documentos_qtd: "Qtd. anexos",
  documentos_tipos: "Tipos enviados", documentos_faltando: "Faltando (obrig.)",
  incompleto: "Dossiê incompleto",
};

const MONEY_KEYS = new Set(["renda_responsavel", "renda_conjuge", "outras_rendas", "renda_total"]);
const BOOL_KEYS = new Set(["area_de_risco", "possui_pcd", "possui_idoso", "recebe_bolsa_familia", "apta_faixa1", "incompleto"]);

function fmt(key, v) {
  if (v === null || v === undefined || v === "") return "—";
  if (key === "cpf") return formatCPF(v);
  if (MONEY_KEYS.has(key)) return formatMoney(Number(v || 0));
  if (BOOL_KEYS.has(key)) return v ? "Sim" : "Não";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  return String(v);
}

export default function CompararDossiesPage() {
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const comparar = async () => {
    const qa = a.trim(); const qb = b.trim();
    if (!qa || !qb) { toast.error("Informe os dois protocolos/CPFs"); return; }
    setLoading(true); setResult(null);
    try {
      const r = await api.get("/comparar-inscricoes", { params: { a: qa, b: qb } });
      setResult(r.data);
    } catch (e) {
      logger.error("Falha ao comparar", e);
      toast.error(e.response?.data?.detail || "Falha ao comparar");
    } finally { setLoading(false); }
  };

  const diffSet = new Set(result?.diff || []);

  return (
    <div className="space-y-6" data-testid="comparar-page">
      <div>
        <h1 className="text-3xl font-black text-[#083CA6] flex items-center gap-2">
          <GitCompare className="w-7 h-7" /> Comparar Dossiês
        </h1>
        <p className="text-sm text-slate-600">
          Confronte 2 inscrições lado a lado. Campos divergentes aparecem destacados em amarelo.
        </p>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Selecione as inscrições</CardTitle></CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-5 gap-3 items-end">
            <div className="md:col-span-2">
              <label className="text-xs text-slate-600 font-semibold">Inscrição A (protocolo ou CPF)</label>
              <Input
                value={a}
                onChange={(e) => setA(e.target.value)}
                placeholder="GRAJAU-MCMV-2026-000001 ou 12345678901"
                data-testid="input-a"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-slate-600 font-semibold">Inscrição B (protocolo ou CPF)</label>
              <Input
                value={b}
                onChange={(e) => setB(e.target.value)}
                placeholder="GRAJAU-MCMV-2026-000002 ou 10987654321"
                data-testid="input-b"
              />
            </div>
            <Button
              onClick={comparar}
              className="bg-[#083CA6] hover:bg-[#062f85]"
              disabled={loading}
              data-testid="btn-comparar"
            >
              {loading
                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                : <Search className="w-4 h-4 mr-2" />}
              Comparar
            </Button>
          </div>
        </CardContent>
      </Card>

      {result && (
        <>
          <Card className="border-sky-200 bg-sky-50/40">
            <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm">
                <strong>{diffSet.size}</strong> campo(s) divergente(s) entre as inscrições.
              </div>
              <div className="flex gap-2 text-xs">
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Iguais
                </Badge>
                <Badge className="bg-amber-100 text-amber-800 border-amber-300">
                  <AlertTriangle className="w-3 h-3 mr-1" /> Divergentes
                </Badge>
              </div>
            </CardContent>
          </Card>

          <div className="grid md:grid-cols-2 gap-4">
            {["a", "b"].map((side) => (
              <Card key={side} data-testid={`card-${side}`} className="border-slate-200">
                <CardHeader className="pb-2">
                  <CardDescription className="text-xs uppercase tracking-wider">
                    Inscrição {side.toUpperCase()}
                  </CardDescription>
                  <CardTitle className="text-lg text-[#083CA6]">
                    {result[side].nome_completo}
                  </CardTitle>
                  <div className="text-xs text-slate-500 font-mono">
                    {result[side].protocolo} • {formatCPF(result[side].cpf)}
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>

          {SECTIONS.map((sec) => (
            <Card key={sec.title}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-bold text-slate-700">{sec.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="divide-y">
                  {sec.keys.map((k) => {
                    const diff = diffSet.has(k);
                    return (
                      <div
                        key={k}
                        className={`grid grid-cols-12 gap-3 py-2 text-sm ${diff ? "bg-amber-50 -mx-6 px-6" : ""}`}
                        data-testid={`row-${k}`}
                      >
                        <div className="col-span-4 text-slate-600 font-medium">
                          {LABELS[k] || k}
                          {diff && <AlertTriangle className="w-3 h-3 inline ml-1 text-amber-600" />}
                        </div>
                        <div className={`col-span-4 ${diff ? "font-bold text-amber-900" : "text-slate-800"}`}>
                          {fmt(k, result.a[k])}
                        </div>
                        <div className={`col-span-4 ${diff ? "font-bold text-amber-900" : "text-slate-800"}`}>
                          {fmt(k, result.b[k])}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </>
      )}
    </div>
  );
}
